import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { buildPatch } from './governedEdit';
import useWarehouseEditAccess from './useWarehouseEditAccess';

const optionsFor = (field, options) => {
  if (field.optionsFrom === 'locationGroups') return (options.locationGroups || []).map((row) => ({ value: row.name, label: row.name }));
  if (field.optionsFrom === 'capacityProfiles') return (options.capacityProfiles || []).map((row) => ({ value: row.profile_id, label: row.profile_id }));
  return (field.options || []).map((value) => ({ value, label: value }));
};

const fieldValue = (record, field) => record?.[field.key] ?? (field.type === 'checkbox' ? false : '');

const GovernedEditForm = ({ title, fields, record, options = {}, endpoint, onSaved }) => {
  const canEdit = useWarehouseEditAccess();
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(Object.fromEntries(fields.map((field) => [field.key, fieldValue(record, field)])));
    setReason('');
    setError('');
    setSaved(false);
  }, [fields, record]);

  const patch = useMemo(() => buildPatch(record || {}, form, reason), [form, reason, record]);
  const changed = Object.keys(patch).some((key) => key !== 'reason');

  if (!canEdit) return null;

  const save = async () => {
    if (!reason.trim() || !changed) return;
    setError('');
    try {
      const { data } = await axios.patch(endpoint, patch);
      onSaved?.(data);
      setExpanded(false);
      setSaved(true);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to save governed change');
    }
  };

  return (
    <section className="mt-4 border-t border-gray-200 pt-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">{title}</p>
        {!expanded && <button type="button" onClick={() => { setSaved(false); setExpanded(true); }} className="rounded border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:border-gray-900">Edit</button>}
      </div>
      {saved && !expanded && <p className="mt-2 text-xs text-emerald-700">Recorded in change log</p>}
      {expanded && <div className="mt-2 space-y-2">
        {fields.map((field) => {
          const value = form[field.key];
          const optionsList = optionsFor(field, options);
          const setValue = (next) => setForm((current) => ({ ...current, [field.key]: next }));
          const controlType = field.key === 'manual_capacity' && typeof record?.manual_capacity === 'boolean' ? 'checkbox' : field.type;
          if (controlType === 'checkbox') {
            return <label key={field.key} className="flex items-center gap-2 text-xs text-gray-700"><input type="checkbox" checked={Boolean(value)} onChange={(event) => setValue(event.target.checked)} />{field.label || field.key}</label>;
          }
          if (controlType === 'textarea') {
            return <label key={field.key} className="block text-xs text-gray-600"><span>{field.label || field.key}</span><textarea value={value ?? ''} onChange={(event) => setValue(event.target.value)} rows="2" className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-xs text-gray-900" /></label>;
          }
          if (controlType === 'select') {
            return <label key={field.key} className="block text-xs text-gray-600"><span>{field.label || field.key}</span><select value={value ?? ''} onChange={(event) => setValue(event.target.value)} className="mt-1 block w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900"><option value="">{field.allowBlank ? 'Not assigned' : 'Select'}</option>{optionsList.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>;
          }
          return <label key={field.key} className="block text-xs text-gray-600"><span>{field.label || field.key}</span><input type={controlType === 'number' ? 'number' : 'text'} value={value ?? ''} onChange={(event) => setValue(event.target.value)} className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-xs text-gray-900" /></label>;
        })}
        <label className="block text-xs text-gray-600"><span>Reason for change</span><textarea value={reason} onChange={(event) => setReason(event.target.value)} rows="2" required className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-xs text-gray-900" /></label>
        {error && <p className="text-xs text-red-700">{error}</p>}
        <div className="flex justify-end gap-2"><button type="button" onClick={() => setExpanded(false)} className="rounded border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700">Cancel</button><button type="button" onClick={save} disabled={!reason.trim() || !changed} className="rounded bg-accent/20 px-2.5 py-1.5 text-xs font-medium text-gray-900 disabled:opacity-40">Save</button></div>
      </div>}
    </section>
  );
};

export default GovernedEditForm;
